import CheckCircle from "@mui/icons-material/CheckCircle";
import { Box, Button } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUpdateSubmissionStatus } from "hooks/useSubmissionMutations";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback, useState } from "react";

/**
 * Staff escape hatch: until the RCRAInfo filing automation ships, a submission
 * can sit in PROCESSING_SUBMISSION indefinitely, so staff can mark it filed
 * manually. Reaching PROCESSING_SUBMISSION already required every review to be
 * approved (or the explicit no-review opt-out), so no extra approval gate is
 * needed. `useUpdateSubmissionStatus` refetches GetSubmissionStepper, flipping
 * the frozen overview to the filed state. Renders nothing for non-staff or
 * once the submission is already filed.
 */
export function MarkFilingCompleteAction() {
  const alerts = useAlerts();
  const { isStaff } = useCurrentUser();
  const { submission, submissionId } = useWasteStepper();
  const [updateSubmissionStatus, { loading: markingComplete }] = useUpdateSubmissionStatus();
  const [markCompleteOpen, setMarkCompleteOpen] = useState(false);

  const handleMarkFilingComplete = useCallback(async () => {
    try {
      await updateSubmissionStatus({
        variables: {
          input: {
            submissionId,
            submissionStatus: SubmissionStatus.FilingComplete,
          },
        },
      });
      alerts.success("Marked as filing complete.");
      setMarkCompleteOpen(false);
    } catch (error) {
      alerts.error("Failed to mark as filing complete.", error);
    }
  }, [updateSubmissionStatus, submissionId, alerts]);

  if (!isStaff || submission?.status !== SubmissionStatus.ProcessingSubmission) {
    return null;
  }

  return (
    <Box>
      <Button
        variant="contained"
        color="success"
        startIcon={<CheckCircle />}
        onClick={() => setMarkCompleteOpen(true)}
      >
        Mark as filing complete
      </Button>
      <ConfirmDialog
        open={markCompleteOpen}
        loading={markingComplete}
        onClose={() => setMarkCompleteOpen(false)}
        onConfirm={handleMarkFilingComplete}
        header="Confirm Status Update"
        info="Are you sure you want to mark this submission as Filing Complete? This action cannot be undone."
        confirmText="Mark as Complete"
        cancelText="Cancel"
      />
    </Box>
  );
}
